
import React, { useState, useEffect } from 'react';
import { Service, LandingSection, PortfolioItem, AppSettings, KaosSettings } from '../types';
import { Icon } from './Icon';
import { saveAppData, auth, signOut, getVisitorStats } from '../firebase';
import { KaosAdminModule } from './KaosAdminModule';

interface AdminPanelProps {
  services: Service[];
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
  sections: LandingSection[];
  setSections: React.Dispatch<React.SetStateAction<LandingSection[]>>;
  portfolios: PortfolioItem[];
  setPortfolios: React.Dispatch<React.SetStateAction<PortfolioItem[]>>;
  appSettings: AppSettings;
  setAppSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
  kaosSettings: KaosSettings;
  setKaosSettings: React.Dispatch<React.SetStateAction<KaosSettings>>;
  isOpen: boolean;
  onClose: () => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({
  services,
  setServices,
  sections,
  setSections,
  portfolios,
  setPortfolios,
  appSettings,
  setAppSettings,
  kaosSettings,
  setKaosSettings,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'traffic' | 'services' | 'sections' | 'portfolio-web' | 'portfolio-app' | 'portfolio-multimedia' | 'portfolio-kaos' | 'settings-kaos' | 'settings'>('services');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  const [visitorLogs, setVisitorLogs] = useState<any[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  useEffect(() => {
    if (activeTab === 'traffic' && isOpen) {
        setIsLoadingLogs(true);
        getVisitorStats(100).then((logs: any[]) => {
            setVisitorLogs(logs);
            setIsLoadingLogs(false);
        });
    }
  }, [activeTab, isOpen]);

  if (!isOpen) return null;

  const handleLogout = async () => {
    if (!showLogoutConfirm) {
      setShowLogoutConfirm(true);
      setTimeout(() => setShowLogoutConfirm(false), 3000);
      return;
    }
    await signOut(auth);
    onClose();
  };

  const handleSaveToFirebase = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    const success = await saveAppData({
      services,
      sections,
      portfolios,
      appSettings,
      kaosSettings,
      lastUpdated: new Date().toISOString()
    });
    setSaveStatus(success ? 'success' : 'error');
    setTimeout(() => setSaveStatus('idle'), 3000);
    setIsSaving(false);
  };

  const updateServiceField = (id: string, field: keyof Service, value: any) => {
    setServices(services.map((s: Service) => s.id === id ? { ...s, [field]: value } : s));
  };

  const addServiceFeature = (id: string) => {
    setServices(services.map((s: Service) => s.id === id ? { ...s, features: [...s.features, 'Fitur Baru'] } : s));
  };

  const updateServiceFeature = (serviceId: string, idx: number, val: string) => {
    setServices(services.map((s: Service) => {
      if (s.id === serviceId) {
        const f = [...s.features];
        f[idx] = val;
        return { ...s, features: f };
      }
      return s;
    }));
  };

  const removeServiceFeature = (serviceId: string, idx: number) => {
    setServices(services.map((s: Service) => s.id === serviceId ? { ...s, features: s.features.filter((_: string, i: number) => i !== idx) } : s));
  };

  const PortfolioList = ({ category }: { category: 'website' | 'aplikasi' | 'multimedia' | 'kaos' }) => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-white uppercase tracking-wider">Kelola Portofolio {category}</h3>
        <button 
          onClick={() => {
            const newItem: PortfolioItem = {
              id: 'p-' + Date.now(),
              title: `Project Baru`,
              description: 'Deskripsi singkat...',
              link: '#',
              category,
              imageUrl: ''
            };
            setPortfolios([...portfolios, newItem]);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold transition-all"
        >
          <Icon name="Plus" size={14} /> Tambah Data
        </button>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {portfolios.filter((p: PortfolioItem) => p.category === category).map((item: PortfolioItem) => (
          <div key={item.id} className="p-5 bg-white/5 border border-white/10 rounded-2xl flex flex-col md:flex-row gap-6 group hover:border-white/20 transition-all">
             <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center relative shrink-0">
                {item.imageUrl ? (
                    <img src={item.imageUrl} className="w-full h-full object-cover" alt="Preview Thumbnail" />
                ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-700">
                        <Icon name="Camera" size={32} />
                        <span className="text-[8px] font-bold">NO IMAGE</span>
                    </div>
                )}
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 rounded text-[8px] font-bold text-white border border-white/5 uppercase">PREVIEW</div>
             </div>
             <div className="flex-1 space-y-4">
                <div className="flex justify-between items-start gap-4">
                   <div className="flex-1">
                      <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block mb-1">Judul Project</label>
                      <input className="w-full bg-transparent border-b border-white/10 text-white font-bold outline-none focus:border-blue-500 transition-all py-1" value={item.title} onChange={(e) => setPortfolios(portfolios.map((p: PortfolioItem) => p.id === item.id ? {...p, title: e.target.value} : p))} />
                   </div>
                   <button onClick={() => setPortfolios(portfolios.filter((p: PortfolioItem) => p.id !== item.id))} className="p-2 text-red-500/30 hover:text-red-500 transition-colors"><Icon name="Trash2" size={18} /></button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                   <div className="space-y-1">
                      <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">URL Thumbnail</label>
                      <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-blue-300 outline-none focus:border-blue-500" value={item.imageUrl || ''} onChange={(e) => setPortfolios(portfolios.map((p: PortfolioItem) => p.id === item.id ? {...p, imageUrl: e.target.value} : p))} placeholder="https://..." />
                   </div>
                   <div className="space-y-1">
                      <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Link Demo / Detail</label>
                      <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 outline-none focus:border-blue-500" value={item.link} onChange={(e) => setPortfolios(portfolios.map((p: PortfolioItem) => p.id === item.id ? {...p, link: e.target.value} : p))} placeholder="https://..." />
                   </div>
                </div>
                <div className="space-y-1">
                    <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Deskripsi Singkat</label>
                    <textarea className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-400 outline-none h-16 resize-none focus:border-blue-500" value={item.description} onChange={(e) => setPortfolios(portfolios.map((p: PortfolioItem) => p.id === item.id ? {...p, description: e.target.value} : p))} placeholder="Ceritakan tentang project ini..." />
                </div>
             </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#111827] w-full max-w-6xl h-[90vh] border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-[#1f2937]">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-purple-600 rounded-xl shadow-lg shadow-purple-900/40"><Icon name="Settings" className="text-white" size={20} /></div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Console Admin</h2>
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Dashboard v1.2</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {saveStatus === 'success' && <span className="text-green-400 text-[10px] font-black animate-pulse uppercase tracking-widest">Data Berhasil Disimpan</span>}
            <button onClick={handleSaveToFirebase} disabled={isSaving} className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-xl text-sm font-bold text-white flex items-center gap-2 shadow-lg shadow-purple-900/40 active:scale-95 transition-all">
              {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Icon name="CheckCircle2" size={16} />}
              Simpan Konten
            </button>
            <button onClick={handleLogout} className={`p-2.5 rounded-xl transition-all ${showLogoutConfirm ? 'bg-red-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}>
              <Icon name="LogOut" size={20} />
            </button>
            <button onClick={onClose} className="p-2 text-slate-500 hover:text-white text-3xl font-light transition-colors">&times;</button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-64 bg-[#0f172a] border-r border-white/5 p-4 flex flex-col gap-1 overflow-y-auto scrollbar-hide">
            {[
              { id: 'traffic', icon: 'Globe', label: 'Traffic Analytics' },
              { id: 'services', icon: 'Layout', label: 'Layanan & Produk' },
              { id: 'portfolio-web', icon: 'Monitor', label: 'Web Portfolio' },
              { id: 'portfolio-app', icon: 'Smartphone', label: 'App Portfolio' },
              { id: 'portfolio-kaos', icon: 'Shirt', label: 'Kaos Gallery' },
              { id: 'portfolio-multimedia', icon: 'Gamepad', label: 'Multimedia' },
              { id: 'settings-kaos', icon: 'Tag', label: 'Price List Kaos' },
              { id: 'sections', icon: 'PenTool', label: 'Struktur Halaman' },
              { id: 'settings', icon: 'Settings', label: 'General Settings' }
            ].map((tab: any) => (
              <button 
                key={tab.id} 
                onClick={() => setActiveTab(tab.id as any)} 
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-sm font-bold ${activeTab === tab.id ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/20' : 'text-slate-500 hover:bg-white/5 hover:text-slate-300'}`}
              >
                <Icon name={tab.icon} size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 p-8 overflow-y-auto bg-[#030712] scrollbar-hide">
            {activeTab === 'traffic' && (
              <div className="space-y-8">
                 <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white tracking-wider">Statistik Pengunjung</h3>
                    <button onClick={() => { setIsLoadingLogs(true); getVisitorStats(100).then((logs: any[]) => { setVisitorLogs(logs); setIsLoadingLogs(false); }); }} className="text-[10px] bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-500 hover:text-white transition-all">SEGARKAN DATA</button>
                 </div>
                 {isLoadingLogs ? <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div></div> : (
                   <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                      <table className="w-full text-left text-[11px]">
                         <thead className="bg-white/5 text-slate-400 uppercase font-bold tracking-widest border-b border-white/5">
                            <tr>
                               <th className="p-4">Waktu</th>
                               <th className="p-4">Perangkat</th>
                               <th className="p-4">Halaman</th>
                               <th className="p-4">Browser</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-white/5 text-slate-300 font-medium">
                            {visitorLogs.map((log: any, i: number) => (
                               <tr key={i} className="hover:bg-white/5 transition-colors">
                                  <td className="p-4 text-slate-500">{new Date(log.timestamp).toLocaleString('id-ID')}</td>
                                  <td className="p-4"><span className={`px-2 py-0.5 rounded text-[10px] font-black ${log.deviceType === 'Mobile' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>{log.deviceType}</span></td>
                                  <td className="p-4 text-white font-bold">{log.pageViewed || 'Landing'}</td>
                                  <td className="p-4 text-slate-400">{log.browser}</td>
                               </tr>
                            ))}
                         </tbody>
                      </table>
                   </div>
                 )}
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">Daftar Layanan Produk</h3>
                  <button onClick={() => setServices([...services, { id: 'svc-'+Date.now(), title:'Nama Layanan', description:'', icon:'Monitor', category:'Software', features:[], priceRange:'Harga...' }])} className="px-4 py-2 bg-purple-600 rounded-lg text-xs font-bold shadow-lg shadow-purple-900/20">+ Tambah Layanan</button>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {services.map((svc: Service) => (
                    <div key={svc.id} className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4 hover:border-white/20 transition-all">
                       <div className="flex gap-4">
                          <div className="flex-1">
                             <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Nama Layanan</label>
                             <input className="w-full bg-transparent border-b border-white/10 text-white font-bold outline-none focus:border-purple-500 transition-all py-1" value={svc.title} onChange={(e) => updateServiceField(svc.id, 'title', e.target.value)} />
                          </div>
                          <div className="w-48">
                             <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Harga / Range</label>
                             <input className="w-full bg-transparent border-b border-white/10 text-purple-400 font-bold outline-none focus:border-purple-500 transition-all py-1" value={svc.priceRange} onChange={(e) => updateServiceField(svc.id, 'priceRange', e.target.value)} />
                          </div>
                          <button onClick={() => setServices(services.filter((s: Service) => s.id !== svc.id))} className="text-red-500/30 hover:text-red-500 mt-4 transition-colors"><Icon name="Trash2" size={18} /></button>
                       </div>
                       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Deskripsi Layanan</label>
                            <textarea className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-slate-400 outline-none h-28 resize-none focus:border-purple-500 transition-all" value={svc.description} onChange={(e) => updateServiceField(svc.id, 'description', e.target.value)} />
                          </div>
                          <div className="p-5 bg-black/20 rounded-2xl border border-white/5">
                             <div className="flex justify-between items-center mb-4">
                                <p className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">Fitur Unggulan (List)</p>
                                <button onClick={() => addServiceFeature(svc.id)} className="text-[9px] bg-purple-600 text-white px-2 py-1 rounded shadow-lg">+ FITUR</button>
                             </div>
                             <div className="space-y-2">
                                {svc.features.map((f: string, fi: number) => (
                                  <div key={fi} className="flex gap-2 group">
                                     <input className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-slate-300 outline-none focus:border-purple-500 transition-all" value={f} onChange={(e) => updateServiceFeature(svc.id, fi, e.target.value)} />
                                     <button onClick={() => removeServiceFeature(svc.id, fi)} className="text-red-500/20 group-hover:text-red-500 transition-colors"><Icon name="X" size={14} /></button>
                                  </div>
                                ))}
                             </div>
                          </div>
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'sections' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white uppercase tracking-wider">Atur Konten Halaman</h3>
                <div className="grid grid-cols-1 gap-4">
                  {sections.map((sec: LandingSection) => (
                    <div key={sec.id} className="p-6 bg-white/5 border border-white/10 rounded-[2rem] flex items-center justify-between gap-8 hover:border-white/20 transition-all">
                       <div className="flex-1 space-y-4">
                          <div className="flex items-center gap-3">
                             <div className={`w-2 h-2 rounded-full ${sec.isVisible ? 'bg-green-500 shadow-lg shadow-green-900/50' : 'bg-slate-700'}`}></div>
                             <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em]">TYPE: {sec.type}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Judul Utama</label>
                                <input className="w-full bg-transparent border-b border-white/10 text-white font-bold outline-none focus:border-purple-500 transition-all py-1" value={sec.title} onChange={(e) => setSections(sections.map((s: LandingSection) => s.id === sec.id ? {...s, title: e.target.value} : s))} />
                             </div>
                             <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Deskripsi / Subtitle</label>
                                <input className="w-full bg-transparent border-b border-white/5 text-slate-400 text-xs outline-none focus:border-purple-500 transition-all py-1" value={sec.subtitle || ''} onChange={(e) => setSections(sections.map((s: LandingSection) => s.id === sec.id ? {...s, subtitle: e.target.value} : s))} placeholder="Slogan atau teks kecil..." />
                             </div>
                          </div>
                       </div>
                       <div className="flex flex-col items-center gap-2">
                          <span className={`text-[9px] font-black uppercase tracking-widest ${sec.isVisible ? 'text-green-500' : 'text-slate-600'}`}>{sec.isVisible ? 'AKTIF' : 'MATI'}</span>
                          <button 
                              onClick={() => setSections(sections.map((s: LandingSection) => s.id === sec.id ? {...s, isVisible: !s.isVisible} : s))}
                              className={`w-14 h-7 rounded-full relative transition-all duration-300 ${sec.isVisible ? 'bg-purple-600' : 'bg-slate-800'}`}
                          >
                              <div className={`absolute top-1.5 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-300 ${sec.isVisible ? 'left-8' : 'left-1.5'}`}></div>
                          </button>
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
               <div className="space-y-12 max-w-2xl pb-10">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">Identitas Bisnis</h3>
                  <div className="space-y-8">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                           <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block ml-1">Nama Brand</label>
                           <input className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white outline-none focus:border-purple-500 transition-all shadow-inner" value={appSettings.brandName} onChange={(e) => setAppSettings({...appSettings, brandName: e.target.value})} />
                        </div>
                        <div className="space-y-3">
                           <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block ml-1">Nomor WA (62...)</label>
                           <input className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white outline-none focus:border-purple-500 transition-all shadow-inner" value={appSettings.contactWhatsapp} onChange={(e) => setAppSettings({...appSettings, contactWhatsapp: e.target.value})} />
                        </div>
                     </div>
                     <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block ml-1">Email Perusahaan</label>
                        <input className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white outline-none focus:border-purple-500 transition-all shadow-inner" value={appSettings.contactEmail} onChange={(e) => setAppSettings({...appSettings, contactEmail: e.target.value})} />
                     </div>
                     <div className="grid grid-cols-2 gap-8 p-8 bg-white/5 rounded-[2.5rem] border border-white/5">
                        <div className="space-y-4">
                           <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest block">Warna Utama</label>
                           <div className="flex items-center gap-4">
                              <input type="color" className="w-12 h-12 rounded-xl bg-transparent border-none cursor-pointer p-0" value={appSettings.primaryColor} onChange={(e) => setAppSettings({...appSettings, primaryColor: e.target.value})} />
                              <span className="text-xs font-mono text-slate-500 uppercase">{appSettings.primaryColor}</span>
                           </div>
                        </div>
                        <div className="space-y-4">
                           <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest block">Warna Sekunder</label>
                           <div className="flex items-center gap-4">
                              <input type="color" className="w-12 h-12 rounded-xl bg-transparent border-none cursor-pointer p-0" value={appSettings.secondaryColor} onChange={(e) => setAppSettings({...appSettings, secondaryColor: e.target.value})} />
                              <span className="text-xs font-mono text-slate-500 uppercase">{appSettings.secondaryColor}</span>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            )}

            {activeTab === 'settings-kaos' && <KaosAdminModule settings={kaosSettings} setSettings={setKaosSettings} />}
            {activeTab === 'portfolio-web' && <PortfolioList category="website" />}
            {activeTab === 'portfolio-app' && <PortfolioList category="aplikasi" />}
            {activeTab === 'portfolio-kaos' && <PortfolioList category="kaos" />}
            {activeTab === 'portfolio-multimedia' && <PortfolioList category="multimedia" />}
          </div>
        </div>
      </div>
    </div>
  );
};
